<template lang="html">
  <div class="fsd-footer">
    <div class="fsd-footer-actions">
      <div
        v-tracking-context="{
          content_element: 'result_list_footer',
          event_element: 'save_search'
        }"
        class="fsd-left"
      >
        <button
          v-click-track="{
            action_type: isUserFullyLoggedIn
              ? 'save_search'
              : 'login_and_save_search'
          }"
          class="button button--primary fsd-footer-actions__save"
          @click.prevent="onSave"
        >
          {{ buttonText | t }}
        </button>
      </div>
      <div
        v-tracking-context="{ content_element: 'ad_footer' }"
        class="fsd-right fsd-right--centered fsd-footer-actions__right"
      >
        <div
          v-tracking-context="{ event_element: 'ad_footer_actions' }"
          class="fsd-footer-actions__bar"
        >
          <span class="fsd-footer-actions__misc">
            <button
              v-if="!isMobile"
              v-click-track="{ action_type: 'print', id: jobId }"
              class="fsd-footer-actions__print"
              :disabled="!job"
              @click.prevent="print"
            >
              <svg-icon icon-name="print-o" />
            </button>
            <button
              ref="share"
              v-click-track="{ action_type: 'share_menu_toggle', id: jobId }"
              class="fsd-footer-actions__share"
              :disabled="!job"
              @click.prevent="onToggleShare"
            >
              <svg-icon icon-name="share" />
            </button>
            <button
              v-click-track="{
                action_type: `bookmark_toggle_${
                  job && isBookmarked(job.id) ? 'off' : 'on'
                }`,
                id: jobId
              }"
              class="fsd-footer-actions__bookmark"
              :class="{
                'fsd-footer-actions__bookmark--active':
                  job && isBookmarked(job.id)
              }"
              :disabled="!job"
              @click.prevent="onBookmarkToggleClick"
            >
              <svg-icon icon-name="bookmark" />
            </button>

            <transition name="fade">
              <nav
                v-if="shareVisible"
                v-tracking-context="{ event_element: 'share_menu' }"
                class="fsd-footer-actions__share-list"
              >
                <a
                  v-for="(val, key) in shareOptions"
                  :key="key"
                  v-click-track="{ action_type: key, id: jobId }"
                  class="fsd-footer-actions__share-option"
                  :href="val"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                >
                  <svg-icon :icon-name="key" />
                  {{ `client.fused.footer.share.${key}` | t }}
                </a>
                <a
                  v-if="isMobile"
                  v-click-track="{ action_type: 'print', id: jobId }"
                  class="fsd-footer-actions__share-option"
                  @click.prevent="print"
                >
                  <svg-icon icon-name="print-o" />
                  {{ 'client.fused.job.print' | t }}
                </a>
              </nav>
            </transition>
          </span>
          <button
            class="fsd-footer-actions__nav"
            :disabled="!isResultsPrevAvailable"
            @click.prevent="
              tracked($event, resultsSelectPrev, { action_type: 'visit_prev' })
            "
          >
            <svg-icon
              icon-name="caret-right"
              class-name="icon--flip icon--set-position"
            />
          </button>
          <apply-button
            v-bind="applyButtonProps"
            classes="fsd-footer-actions__apply"
            :disabled="!job || isJobExpired"
            button-classes="fsd-footer-actions__apply-button"
          />
          <button
            class="fsd-footer-actions__nav"
            :disabled="!isResultsNextAvailable"
            @click.prevent="
              tracked($event, resultsSelectNext, { action_type: 'visit_next' })
            "
          >
            <svg-icon icon-name="caret-right" class-name="icon--set-position" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _debounce from 'lodash-es/debounce'

import { mapGetters, mapState, mapActions, mapMutations } from 'vuex'

import ApplyButton from '@/job/apply_button.vue'
import { manualTrack } from '@/vue/directives/tracking'

export default {
  components: { ApplyButton },
  data: () => ({
    shareVisible: false,
    mediaQuery: null
  }),
  computed: {
    ...mapState(['job', 'resultsFenceUrl', 'initialIntent']),
    ...mapGetters([
      'isJobExpired',
      'isBookmarked',
      'isResultsNextAvailable',
      'isResultsPrevAvailable',
      'isUserFullyLoggedIn',
      'isUserKnown'
    ]),
    jobId() {
      return (this.job || {}).id
    },
    applyButtonProps() {
      if (!this.job) {
        return {}
      }

      return {
        applyEmail: this.job.data.applyEmail,
        applyUrl: this.job.data.applyUrl,
        directApply: this.job.data.directApply,
        jobId: this.job && '' + this.job.id,
        jobTitle: this.job.data.title,
        jobEmployer: this.job.data.employer
      }
    },
    shareOptions() {
      return this.job?.share
    },
    buttonText() {
      if (this.isUserFullyLoggedIn) {
        return 'client.fused.footer.save_search'
      }
      return 'client.fused.footer.login_and_save_search'
    },
    isMobile() {
      return this.mediaQuery?.current === 'small'
    }
  },
  mounted() {
    // only for mount with initialIntent via oauth
    this.fullfillInitialIntent()
    this.$VueEventBus.on('globalBlur', this.onGlobalBlur)
    const {
      MediaQuery
    } = require('foundation-sites/js/foundation.util.mediaQuery')
    MediaQuery._init()
    this.mediaQuery = MediaQuery
  },
  destroyed() {
    this.$VueEventBus.off('globalBlur', this.onGlobalBlur)
  },
  methods: {
    ...mapActions([
      'resultsSelectNext',
      'resultsSelectPrev',
      'bookmarksToggle',
      'saveSearchProfile',
      'fullfillInitialIntent'
    ]),
    ...mapMutations(['modalOpen']),
    onSave() {
      if (this.isUserFullyLoggedIn) {
        this.saveSearchProfile()
      } else {
        EventBus.trigger('sessionModal:open', {
          intent: 'saveSearchProfile',
          intentData: null
        })
      }
    },
    onToggleShare($event) {
      // We'll tag the click event so we don't blur (and therefore close the share dropdown) on our own event
      $event.toggleShare = true
      this.toggleShare()
    },
    onGlobalBlur($event) {
      if (!$event.toggleShare && this.shareVisible) {
        this.toggleShare()
      }
    },
    onBookmarkToggleClick: _debounce(
      function () {
        if (this.job && this.isUserFullyLoggedIn) {
          this.bookmarksToggle(this.job.id)
        } else {
          EventBus.trigger('sessionModal:open', {
            intent: 'toggleBookmark',
            intentData: this.job.id
          })
        }
      },
      300,
      {
        leading: true,
        trailing: false
      }
    ),
    toggleShare() {
      // If the browser supports the Web Share API, we'll use that
      if (typeof navigator.share === 'function') {
        navigator
          .share({ url: this.job.url, title: this.job.title })
          .catch(error => {
            // browser throws this if user canceled the share operation
            if (!error.toString().includes('AbortError')) {
              return Promise.reject(error)
            }
          })
        // Otherwise we'll show this ugly ass dropdown
      } else {
        this.shareVisible = !this.shareVisible
      }
    },
    print() {
      window.print()
    },
    tracked(e, action, trackConfig) {
      manualTrack(e.currentTarget, trackConfig)
      action()
    }
  }
}
</script>
